import { ExternalLink } from 'lucide-react';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PageHeader } from '../../components/common/PageHeader';
import { appRoutes } from '../../app-routes';
import { EngageSettingsTab } from './engage-settings-tab';
import { SegmentsTab } from '../feature-setup/segments-tab';
import { AutomationsTab } from '../feature-setup/automations-tab';
import { EngageTenantsTab } from './engage-tenants-tab';
import { EngageMetrics } from '../metrics/tabs/EngageMetrics';

type TabName = 'usage' | 'tenants' | 'settings' | 'segments' | 'automations';

const VALID_TABS: TabName[] = ['usage', 'tenants', 'settings', 'segments', 'automations'];

function getValidTab(tab: string | undefined): TabName {
  if (VALID_TABS.includes(tab as TabName)) return tab as TabName;
  return 'usage';
}

export const EngageSettingsNavigator: React.FC = () => {
  const { tab = 'usage' } = useParams();
  const navigate = useNavigate();

  const activeTab = getValidTab(tab);
  useEffect(() => {
    if (!VALID_TABS.includes(tab as TabName)) {
      navigate(`${appRoutes.ENGAGE_SETUP}/usage`, { replace: true });
    }
  }, [tab, navigate]);

  const handleTabChange = (newTab: string) => {
    const tabToNavigateTo = getValidTab(newTab);
    const newPath = `${appRoutes.ENGAGE_SETUP}/${tabToNavigateTo}`;
    navigate(newPath, { replace: true });
  };

  const components: Record<TabName, React.FC> = {
    usage: EngageMetrics,
    tenants: EngageTenantsTab,
    segments: SegmentsTab,
    automations: AutomationsTab,
    settings: EngageSettingsTab,
  };
  const TabComponent = components[activeTab];

  return (
    <section className="space-y-6">
      <PageHeader
        title="Engage"
        description="Targeted email & SMS campaigns with pre-built automation templates"
        right={
          <a
            href="https://docs.embedreach.com/engage/introduction"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
            API Docs
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        }
      />
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full max-w-2xl grid-cols-5">
          <TabsTrigger value="usage">Usage</TabsTrigger>
          <TabsTrigger value="tenants">Tenants</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="automations">Automations</TabsTrigger>
          <TabsTrigger value="segments">Segments</TabsTrigger>
        </TabsList>
        <TabComponent />
      </Tabs>
    </section>
  );
};
